import type { AnalyticsFilter, Granularity } from "@seed-factory/core";
import { appConfig } from "./config";

export type PeriodPreset =
  | "today"
  | "yesterday"
  | "7d"
  | "30d"
  | "this_month"
  | "last_month"
  | "90d"
  | "this_year"
  | "custom";

export const periodLabels: Record<PeriodPreset, string> = {
  today: "Hoje",
  yesterday: "Ontem",
  "7d": "7 dias",
  "30d": "30 dias",
  this_month: "Este mês",
  last_month: "Mês anterior",
  "90d": "90 dias",
  this_year: "Este ano",
  custom: "Personalizado",
};

type CivilDateParts = { year: number; month: number; day: number };

function parseCivilDate(value: string): CivilDateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (!match) throw new Error("INVALID_CIVIL_DATE");
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    throw new Error("INVALID_CIVIL_DATE");
  }
  return { year, month, day };
}

function civilFromUtcToken(date: Date): string {
  const year = String(date.getUTCFullYear()).padStart(4, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function zoneParts(date: Date, timeZone: string) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const values = new Map(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  return {
    year: Number(values.get("year")),
    month: Number(values.get("month")),
    day: Number(values.get("day")),
    hour: Number(values.get("hour")),
    minute: Number(values.get("minute")),
    second: Number(values.get("second")),
  };
}

function zoneOffsetMilliseconds(date: Date, timeZone: string): number {
  const parts = zoneParts(date, timeZone);
  const zonedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return zonedAsUtc - (date.getTime() - date.getMilliseconds());
}

export function civilDateValue(date: Date, timeZone = appConfig.timezone): string {
  const parts = zoneParts(date, timeZone);
  return `${String(parts.year).padStart(4, "0")}-${String(parts.month).padStart(2, "0")}-${String(
    parts.day,
  ).padStart(2, "0")}`;
}

export function dateForCivilDay(
  value: string,
  endOfDay = false,
  timeZone = appConfig.timezone,
): Date {
  const { year, month, day } = parseCivilDate(value);
  const target = Date.UTC(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
  let result = new Date(target);
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const candidate = new Date(target - zoneOffsetMilliseconds(result, timeZone));
    if (candidate.getTime() === result.getTime()) break;
    result = candidate;
  }
  return result;
}

export function rangeForCivilDates(
  from: string,
  to: string,
  timeZone = appConfig.timezone,
): Pick<AnalyticsFilter, "from" | "to"> {
  const start = dateForCivilDay(from, false, timeZone);
  const end = dateForCivilDay(to, true, timeZone);
  if (start > end) throw new Error("INVALID_DATE_RANGE");
  return { from: start, to: end };
}

export function shiftCivilDays(value: string, days: number): string {
  const { year, month, day } = parseCivilDate(value);
  const token = new Date(Date.UTC(year, month - 1, day));
  token.setUTCDate(token.getUTCDate() + days);
  return civilFromUtcToken(token);
}

export function civilMonthStart(value: string): string {
  const { year, month } = parseCivilDate(value);
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
}

export function shiftCivilMonths(value: string, months: number): string {
  const start = civilMonthStart(value);
  const { year, month } = parseCivilDate(start);
  const token = new Date(Date.UTC(year, month - 1, 1));
  token.setUTCMonth(token.getUTCMonth() + months);
  return civilFromUtcToken(token);
}

export function civilWeekStart(value: string): string {
  const { year, month, day } = parseCivilDate(value);
  const token = new Date(Date.UTC(year, month - 1, day));
  const mondayOffset = (token.getUTCDay() + 6) % 7;
  token.setUTCDate(token.getUTCDate() - mondayOffset);
  return civilFromUtcToken(token);
}

export function civilBucketLabel(value: string, granularity: Granularity): string {
  const { year, month, day } = parseCivilDate(value);
  if (granularity === "month") {
    const months = [
      "jan",
      "fev",
      "mar",
      "abr",
      "mai",
      "jun",
      "jul",
      "ago",
      "set",
      "out",
      "nov",
      "dez",
    ];
    return `${months[month - 1] ?? String(month).padStart(2, "0")}/${String(year).slice(-2)}`;
  }
  const label = `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}`;
  return granularity === "week" ? `S ${label}` : label;
}

export function rangeForPreset(
  preset: PeriodPreset,
  now = new Date(),
  timeZone = appConfig.timezone,
): Pick<AnalyticsFilter, "from" | "to"> {
  const today = civilDateValue(now, timeZone);
  switch (preset) {
    case "today":
      return rangeForCivilDates(today, today, timeZone);
    case "yesterday": {
      const day = shiftCivilDays(today, -1);
      return rangeForCivilDates(day, day, timeZone);
    }
    case "7d":
      return rangeForCivilDates(shiftCivilDays(today, -6), today, timeZone);
    case "30d":
      return rangeForCivilDates(shiftCivilDays(today, -29), today, timeZone);
    case "this_month":
      return rangeForCivilDates(civilMonthStart(today), today, timeZone);
    case "last_month": {
      const currentMonth = civilMonthStart(today);
      const previousMonth = shiftCivilMonths(currentMonth, -1);
      const end = shiftCivilDays(currentMonth, -1);
      return rangeForCivilDates(previousMonth, end, timeZone);
    }
    case "90d":
      return rangeForCivilDates(shiftCivilDays(today, -89), today, timeZone);
    case "this_year":
      return rangeForCivilDates(`${today.slice(0, 4)}-01-01`, today, timeZone);
    case "custom":
      return rangeForCivilDates(shiftCivilDays(today, -29), today, timeZone);
  }
}

export const granularityLabels: Record<Granularity, string> = {
  day: "Dia",
  week: "Semana",
  month: "Mês",
};
